import React, { useState, useEffect } from "react";
import { Users, User, Calendar, Clock, AlertCircle } from "lucide-react";
import { useGetRegularizationStatsQuery } from "../../store/apis/attendanceRegularization.api";
import HolidayCard from "../holiday/HolidayCard";
import EmployeeCard from "./EmployeeCard";
import BirthdayAnniversaryCard from "./BirthdayAnniversaryCard";
import RecentJoinersCard from "./RecentJoinersCard";
import LeaveSummaryCard from "./LeaveSummaryCard";
import MyCalendar, { CalendarCardSkeleton } from "./MyCalendar";
import { WorkflowQueryStatus } from "../../utils/constants";
import {
  BirthdayAnniversaryCardSkeleton,
  HolidaysCardSkeleton,
  MonthlyJoinersSkeleton,
  StatCardSkeleton,
  TeamStatusSkeleton,
  EmployeeInfoSkeleton,
} from "./Skeleton";
import { RoleTypeEnum } from "../../utils/constants";
import { formatDate } from "../../utils/timeUtils";
import { capitalizeWords } from "../../utils/nameUtils";
import { useSignedUrl } from "../../store/hooks/useSignedUrl";
import { SimpleTooltip } from "../common";
import { useAuth } from "../../store/hooks/useAuth";
import { useGetTodayStatusQuery } from "../../store/apis/attendance.api";
import { useGetStatsQuery, useGetUserByIdQuery } from "../../store/apis/user.api";
import { useGetLeavesQuery } from "../../store/apis/leave.api";
import { useGetTimesheetStatsQuery } from "../../store/apis/timesheet.api";

// ✅ 1. NATIVE BRIDGE TYPE DEFINITION
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const currentDate = new Date();

  // ✅ V2 API Hooks
  const { data: userStats, isLoading: loadingUserStats } = useGetStatsQuery();
  const { data: timesheetStats, isLoading: loadingTimesheetStats } = useGetTimesheetStatsQuery();
  const { data: pendingLeaves, isLoading: loadingPendingLeaves } = useGetLeavesQuery({
    status: WorkflowQueryStatus.PENDING,
  });
  const { data: todayStatusResponse } = useGetTodayStatusQuery(undefined, { skip: !user?.id });

  const { data: arStats, isLoading: loadingArStats } = useGetRegularizationStatsQuery();
  const arPendingCount = arStats?.pending || 0;

  const isSuperAdmin = user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN.toLowerCase();
  const { data: employeeData, isLoading: loadingEmployee } = useGetUserByIdQuery(user?.id || "", {
    skip: !user?.id || isSuperAdmin,
  });
  const profilePicSrc = useSignedUrl(employeeData?.profilePic);
  const [imgError, setImgError] = useState(false);

  const loading = loadingUserStats || loadingTimesheetStats || loadingPendingLeaves || loadingEmployee;

  const capitalize = (value = "") =>
    value
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  // Native bridge sync using v2 today-status from Redux
  useEffect(() => {
    if (!user?.id) return;
    const statusData = todayStatusResponse?.data;
    const isActive = !!statusData?.checkInTime && !statusData?.checkOutTime;
    const bridge = window.ReactNativeWebView;
    if (bridge) {
      const messageType = isActive ? "START_MOBILE_SERVICES" : "STOP_MOBILE_SERVICES";
      bridge.postMessage(
        JSON.stringify({
          type: messageType,
          payload: {
            status: isActive ? "checked_in" : "checked_out",
            source: "admin_dashboard_sync",
          },
        })
      );
      console.log(`📦 NATIVE BRIDGE (ADMIN): Sync Fired -> ${messageType}`);
    }
  }, [user?.id, todayStatusResponse]);
  const stats = React.useMemo(() => {
    return [
      {
        title: "Total Employees",
        value: String(userStats?.totalEmployees || 0),
        change: "+0%",
        icon: Users,
        iconBg: "bg-primary-50",
        iconColor: "text-primary-600",
        trend: "up",
      },
      {
        title: "Pending Leaves",
        value: String(pendingLeaves?.total || 0),
        change: "+0%",
        icon: Calendar,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        trend: "up",
      },
      {
        title: "Pending Timesheets",
        value: String(timesheetStats?.pending || 0),
        change: timesheetStats?.change || "+0%",
        icon: Clock,
        iconBg: "bg-secondary-50",
        iconColor: "text-secondary-600",
        trend: "up",
      },
      {
        title: "AR Pending",
        value: String(arPendingCount),
        change: "+0%",
        icon: AlertCircle,
        iconBg: "bg-orange-50",
        iconColor: "text-orange-600",
        trend: "up",
      },
    ];
  }, [userStats, pendingLeaves, timesheetStats, arPendingCount]);

  const statCards = Array.from({ length: 4 });

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {loading
          ? statCards.map((_, index) => <StatCardSkeleton key={`skeleton-${index}`} />)
          : stats.map((stat) => (
            <div
              key={stat.title}
              className="bg-white rounded-xl shadow-soft border border-slate-100 p-3 sm:p-5 lg:p-6 hover:shadow-soft-hover hover:-translate-y-1 transition-all duration-200"
            >
              <div className="flex px-0 sm:flex-row sm:items-start sm:justify-between h-full">
                <div className="flex-1 min-w-0 mb-3 sm:mb-0 pr-2 card-content">
                  <p className="text-xs sm:text-sm font-semibold text-slate-500 mb-1 sm:mb-2 break-words leading-tight card-title">
                    {stat.title}
                  </p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-0 leading-tight card-value tracking-tight">
                    {stat.value}
                  </p>
                </div>

                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl ${stat.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                  <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {!isSuperAdmin && (employeeData || loadingEmployee) && (
          loadingEmployee ? (
            <EmployeeInfoSkeleton />
          ) : (
            <div className="bg-white rounded-xl shadow-soft border border-slate-100 p-4 sm:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem] flex flex-col hover:shadow-soft-hover transition-all duration-200">
              {/* Card Header */}
              <div className="flex items-center justify-between mb-4 shrink-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2 tracking-tight">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  Employee Information
                </h2>
              </div>

              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Section (Profile Pic & Name) */}
                <div className="flex items-start space-x-4 shrink-0 pb-4">
                  <div className="flex-shrink-0">
                    {employeeData?.profilePic && !imgError ? (
                      <img
                        src={profilePicSrc}
                        alt="Profile Picture"
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center shadow-brand-button">
                        <span className="text-white text-xl sm:text-2xl font-extrabold">
                          {employeeData?.firstName?.charAt(0) || "U"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1 tracking-tight">
                      {capitalize(employeeData?.firstName || "")}
                      {employeeData?.lastName ? ` ${capitalize(employeeData?.lastName)}` : ""}
                    </h3>
                    <p className="text-sm text-slate-500 mb-1 ">
                      <SimpleTooltip side="bottom" delay={500} className="truncate" label={employeeData?.workEmail || "N/A"}>
                        {employeeData?.workEmail || "N/A"}
                      </SimpleTooltip>
                    </p>
                    <p className="text-sm text-slate-500">{employeeData?.employeeId}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 flex-1 overflow-y-auto scrollbar-thin pr-2">
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Department:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {capitalizeWords(
                          employeeData?.departmentName ?? employeeData?.department ?? ""
                        ) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Position:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {capitalizeWords(
                          employeeData?.designationName ?? employeeData?.designation ?? ""
                        ) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Functional Manager:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {capitalizeWords(employeeData?.functionalManagerName) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Reporting Manager:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {capitalizeWords(employeeData?.reportingManagerName) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Phone:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {employeeData?.phone ? `+91 ${employeeData.phone}` : "Not Provided"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-400 shrink-0 font-medium">Date of Joining:</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {employeeData?.joinDate ? formatDate(employeeData?.joinDate) : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? <TeamStatusSkeleton /> : <EmployeeCard />}
        </div>

        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? <CalendarCardSkeleton /> : <MyCalendar />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? <HolidaysCardSkeleton /> : <HolidayCard />}
        </div>

        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? <TeamStatusSkeleton /> : <LeaveSummaryCard />}
        </div>

        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? (
            <BirthdayAnniversaryCardSkeleton />
          ) : (
            <BirthdayAnniversaryCard />
          )}
        </div>
        <div className="h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem]">
          {loading ? <MonthlyJoinersSkeleton /> : <RecentJoinersCard />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;